import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { EgressCalculationResult } from "./spotHelpers";

type Props = {
  open: boolean;
  onClose: () => void;
  result: EgressCalculationResult | null;
};

export const EgressCalculationDialog: React.FC<Props> = ({
  open,
  onClose,
  result,
}) => {
  const { t } = useTranslation();
  if (!result) {
    return null;
  }

  const {
    logs,
    totalSpots,
    spotsWithEgress,
    spotsWithoutEgress,
    totalBlockingRelationships,
  } = result;

  // Sort logs alphabetically by spot name
  const sortedLogs = [...logs].sort((a, b) =>
    a.spotName.localeCompare(b.spotName)
  );

  // Calculate blocked-by counts for each spot
  const blockedByMap = new Map<string, number>();
  sortedLogs.forEach((log) => {
    blockedByMap.set(log.spotId, 0);
  });
  sortedLogs.forEach((log) => {
    log.blocksSpots.forEach((blockedSpotId) => {
      blockedByMap.set(
        blockedSpotId,
        (blockedByMap.get(blockedSpotId) || 0) + 1
      );
    });
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('settings.rampsSettings.rampEditor.egressCalculation.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Summary Statistics */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('settings.rampsSettings.rampEditor.egressCalculation.summary.title')}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Chip label={t('settings.rampsSettings.rampEditor.egressCalculation.summary.totalSpots', { count: totalSpots })} color="primary" />
              <Chip
                label={t('settings.rampsSettings.rampEditor.egressCalculation.summary.withEgressLane', { count: spotsWithEgress })}
                color="success"
              />
              <Chip
                label={t('settings.rampsSettings.rampEditor.egressCalculation.summary.withoutEgressLane', { count: spotsWithoutEgress })}
                color="default"
              />
              <Chip
                label={t('settings.rampsSettings.rampEditor.egressCalculation.summary.blockingRelationships', { count: totalBlockingRelationships })}
                color="warning"
              />
            </Stack>
          </Box>

          {/* Results Table */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.title')}
            </Typography>
            <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>{t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.spot')}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.egressLane')}
                      <Tooltip title={t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.egressLaneTooltip')}>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.hasLane')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.blockingCount')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.blockedByCount')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.headers.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogs.map((log) => {
                  const blockedByCount = blockedByMap.get(log.spotId) || 0;
                  const hasConflict =
                    log.blocksSpots.length > 0 || blockedByCount > 0;

                  // Find which spots block this spot
                  const blockedBySpots = sortedLogs
                    .filter((l) => l.blocksSpots.includes(log.spotId))
                    .map((l) => l.spotName);

                  return (
                    <TableRow
                      key={log.spotId}
                      sx={{
                        backgroundColor: log.skipped
                          ? "action.disabledBackground"
                          : hasConflict
                          ? "warning.lighter"
                          : "success.lighter",
                      }}
                    >
                      <TableCell>{log.spotName}</TableCell>
                      <TableCell>{log.egressLane || t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.noLane')}</TableCell>
                      <TableCell align="center">
                        {log.hasEgressLane ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="disabled" fontSize="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {log.blocksSpots.length > 0 ? (
                          <Tooltip
                            title={t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.blocksTooltip', {
                              spots: log.blocksSpots
                                .map((id) => {
                                  const spot = sortedLogs.find(
                                    (l) => l.spotId === id
                                  );
                                  return spot?.spotName || id.slice(0, 8);
                                })
                                .join(", ")
                            })}
                          >
                            <Chip
                              label={log.blocksSpots.length}
                              size="small"
                              color="warning"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.zeroCount')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {blockedByCount > 0 ? (
                          <Tooltip
                            title={t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.blockedByTooltip', {
                              spots: blockedBySpots.join(", ")
                            })}
                          >
                            <Chip
                              label={blockedByCount}
                              size="small"
                              color="error"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('settings.rampsSettings.rampEditor.egressCalculation.spotAnalysis.zeroCount')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color={
                            log.skipped ? "text.secondary" : "text.primary"
                          }
                        >
                          {log.reason}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Detailed Blocking Relationships */}
          {totalBlockingRelationships > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('settings.rampsSettings.rampEditor.egressCalculation.details.title')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1, fontStyle: "italic" }}
              >
                {t('settings.rampsSettings.rampEditor.egressCalculation.details.gapSpotNote')}
              </Typography>
              <Stack spacing={0.5}>
                {sortedLogs
                  .filter((log) => log.blocksSpots.length > 0)
                  .map((log) => {
                    const blockedNames = log.blocksSpots
                      .map((id) => {
                        const bl = sortedLogs.find((l) => l.spotId === id);
                        return bl?.spotName || id.slice(0, 8);
                      })
                      .join(", ");

                    return (
                      <Typography variant="caption" key={log.spotId}>
                        <Box component="span" sx={{ fontWeight: "bold" }}>
                          {t('settings.rampsSettings.rampEditor.egressCalculation.details.blocksLabel', { spotName: log.spotName })}
                        </Box>{" "}
                        {blockedNames}
                      </Typography>
                    );
                  })}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('settings.rampsSettings.rampEditor.egressCalculation.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
